import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import axios from "axios";
import { DashboardLayout } from "@/pages/Dashboard";
import { useLanguage, useAuth, API } from "@/App";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ListingImage } from "@/components/ListingImage";
import { Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";

const StatusBadge = ({ status, t }) => {
  const cls =
    status === "approved" ? "bg-green-500/20 text-green-400" :
    status === "pending" ? "bg-yellow-500/20 text-yellow-400" :
    status === "rejected" ? "bg-red-500/20 text-red-400" :
    "bg-slate-700 text-slate-300";
  const label = status === "approved" ? t("منشور", "Live") :
    status === "pending" ? t("قيد المراجعة", "Pending") :
    status === "rejected" ? t("مرفوض", "Rejected") :
    t("مؤرشف", "Archived");
  return <Badge className={cls}>{label}</Badge>;
};

export default function MyVehicles() {
  const { t } = useLanguage();
  const { token } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const res = await axios.get(`${API}/vehicles/mine`, { headers: { Authorization: `Bearer ${token}` } });
      setItems(res.data);
    } catch (e) { console.error(e); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const remove = async (id) => {
    if (!window.confirm(t("هل أنت متأكد من الحذف؟", "Are you sure?"))) return;
    try {
      await axios.delete(`${API}/vehicles/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(t("تم الحذف", "Deleted"));
      load();
    } catch (e) { toast.error(t("فشل الحذف", "Delete failed")); }
  };

  return (
    <DashboardLayout title={t("سياراتي", "My Vehicles")}>
      <div className="space-y-4" data-testid="my-vehicles">
        <div className="flex justify-between items-center">
          <Link to="/vehicles" className="text-yellow-500 hover:underline">{t("← العودة للسوق", "← Back to marketplace")}</Link>
          <Button onClick={() => navigate("/vehicles/post")} className="btn-primary"><Plus className="ml-2" size={16} />{t("نشر جديد", "New listing")}</Button>
        </div>
        {loading ? (
          <div className="glass-card rounded-2xl h-40 skeleton" />
        ) : items.length === 0 ? (
          <div className="glass-card rounded-2xl p-12 text-center text-slate-400">{t("ليس لديك سيارات منشورة", "No vehicles yet")}</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((v) => (
              <div key={v.vehicle_id} className="glass-card rounded-2xl overflow-hidden">
                <div onClick={() => navigate(`/vehicles/${v.vehicle_id}`)} className="cursor-pointer">
                  <div className="h-40 overflow-hidden">
                    <ListingImage path={v.images?.[0]} alt={v.title} className="w-full h-full object-cover" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between mb-1">
                      <h3 className="font-bold text-white truncate">{v.brand} {v.model} {v.year}</h3>
                      <StatusBadge status={v.status} t={t} />
                    </div>
                    <p className="text-sm text-slate-400">{v.city}, {v.state}</p>
                    {/* Seller pricing breakdown — only the seller sees these fields */}
                    {v.owner_price !== undefined ? (
                      <div className="mt-2 text-xs leading-tight space-y-0.5">
                        <p className="text-slate-300">{t("سعرك", "Your price")}: <span className="text-white font-bold">${Number(v.owner_price).toLocaleString()}{v.listing_type === "rent" ? `/${t("يوم", "day")}` : ""}</span></p>
                        <p className="text-slate-300">{t("سعر العميل", "Customer pays")}: <span className="text-yellow-400 font-bold">${Number(v.price).toLocaleString()}{v.listing_type === "rent" ? `/${t("يوم", "day")}` : ""}</span></p>
                        {v.partner_bonus !== undefined && (
                          <p className="text-emerald-400">{t("مكافأة الشريك بعد البيع", "Your partner bonus on sale")}: +${Number(v.partner_bonus).toLocaleString()}</p>
                        )}
                      </div>
                    ) : (
                      <p className="text-yellow-400 font-bold mt-1">${v.price?.toLocaleString()}{v.listing_type === "rent" ? `/${t("يوم", "day")}` : ""}</p>
                    )}
                  </div>
                </div>
                <div className="p-3 border-t border-slate-800 flex justify-end">
                  <button onClick={() => remove(v.vehicle_id)} className="text-red-400 hover:text-red-300 flex items-center gap-1 text-sm" data-testid={`delete-veh-${v.vehicle_id}`}>
                    <Trash2 size={14} /> {t("حذف", "Delete")}
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
}
